import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Inbox, AlertTriangle, RefreshCw } from "lucide-react";
import { RelativeTime } from "../ui/RelativeTime";

const INITIAL_VISIBLE = 8;
const VISIBLE_STEP = 30;

// Fallback legible cuando el tipo del request ya no existe en el catálogo de la vista.
export const humanizeReportType = (raw) => {
  if (!raw) return "";
  const label = String(raw).replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const STATUS_CONFIG = {
  queued: { label: "En cola", color: "status-queued" },
  processing: { label: "En progreso", color: "status-processing" },
  ready: { label: "Listo para descargar", color: "status-ready" },
  failed: { label: "Error", color: "status-failed" },
};

const STAGE_LABELS = {
  queued: "En cola",
  consultando_paquetes: "Consultando paquetes",
  consultando_repartidores: "Consultando repartidores",
  consultando_colectas: "Consultando colectas",
  consultando_entregas: "Consultando entregas",
  consultando_incidencias: "Consultando incidencias",
  construyendo_filas: "Construyendo filas",
  procesando_incidencias: "Procesando incidencias",
  generando_archivos: "Generando archivos",
  subiendo_archivos: "Subiendo archivos",
  resolviendo_empresa: "Resolviendo empresa",
  generando_qr: "Generando QR",
  renderizando_template: "Renderizando template",
  generando_pdf: "Generando PDF",
  subiendo_pdf: "Subiendo PDF",
  completado: "Completado",
};

const formatDisplayDate = (isoStr) => {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "-";
  // Relativo ("hace X") con la fecha absoluta en el tooltip nativo del <span>.
  return <RelativeTime value={isoStr} live />;
};

const formatDateOnly = (isoStr, timezone) => {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "-";
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: tz,
  });
};

const helpers = { formatDateOnly, formatDisplayDate };

const handleDownload = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "informe";
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
};

export default function ReportRequestList({
  requests,
  loading,
  onRefresh,
  resolveTypeLabel,
  renderDownloadButtons,
  renderMetadata,
  renderRowCount,
}) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const hasActiveRequests = requests.some(
    (r) => r.status === "queued" || r.status === "processing",
  );
  const visibleRequests = requests.slice(0, visibleCount);
  const remaining = requests.length - visibleRequests.length;

  return (
    <section className="ir-list-card" aria-label={t("Informes solicitados")}>
      <div className="ir-list-header">
        <h3 className="ir-section-title">{t("Informes solicitados")}</h3>
        <div className="ir-list-actions">
          {hasActiveRequests && (
            <span className="ir-processing-badge">
              <span className="ir-pulse" aria-hidden="true" />
              {t("Procesando...")}
            </span>
          )}
          <button
            className="ir-refresh-btn"
            onClick={onRefresh}
            title={t("Actualizar lista")}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ir-empty-state">
          <span className="ir-spinner" aria-hidden="true" />
          <span>{t("Cargando informes...")}</span>
        </div>
      ) : requests.length === 0 ? (
        <div className="ir-empty-state">
          <span className="ir-empty-icon" aria-hidden="true"><Inbox size={40} strokeWidth={1.5} /></span>
          <span>{t("Aún no hay informes solicitados.")}</span>
          <span className="ir-empty-hint">{t("Solicita tu primer informe usando el formulario.")}</span>
        </div>
      ) : (
        <div className="ir-requests-list">
          {visibleRequests.map((req) => {
            const { label: typeLabel, icon: typeIcon } = resolveTypeLabel(req);
            const statusCfg = STATUS_CONFIG[req.status] || {
              label: req.status,
              color: "status-queued",
            };
            const isReady = req.status === "ready";
            const isFailed = req.status === "failed";
            const isActive = req.status === "queued" || req.status === "processing";
            const percentage = req.progress?.percentage ?? 0;
            const stageLabel = STAGE_LABELS[req.progress?.stage] ?? "En cola";

            return (
              <article key={req._id} className="ir-request-item">
                <div className="ir-request-info">
                  <div className="ir-request-top">
                    <span className="ir-request-type">
                      {typeIcon} {typeLabel}
                    </span>
                    <span className={`ir-status-badge ${statusCfg.color}`}>
                      {t(statusCfg.label)}
                    </span>
                  </div>
                  <div className="ir-request-meta">
                    {renderMetadata(req, helpers)}
                    {isReady && renderRowCount?.(req)}
                    {isFailed && req.error && (
                      <span className="ir-meta-item ir-meta-error">
                        <AlertTriangle size={13} aria-hidden="true" /> {req.error}
                      </span>
                    )}
                  </div>
                  {isActive && (
                    <div className="ir-progress-container">
                      <div className="ir-progress-bar">
                        <div
                          className="ir-progress-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="ir-progress-label">
                        {t(stageLabel)} — {percentage}%
                      </span>
                    </div>
                  )}
                </div>
                {isReady && (
                  <div className="ir-download-actions">
                    {renderDownloadButtons(req, handleDownload)}
                  </div>
                )}
              </article>
            );
          })}
          {remaining > 0 && (
            <div className="ir-show-more">
              <button
                className="ir-show-more-btn"
                onClick={() => setVisibleCount((c) => c + VISIBLE_STEP)}
                type="button"
              >
                {t("Ver más ({{n}} restantes)", { n: remaining })}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
